import { useState, useMemo, useEffect } from "react";
import { add, apply, Skills, sum } from "../models/Skills";
import { UserData, xps } from "../models/UserData";

const SKILL_PET_XP_MULTIPLIER = 3;
const COLLECTION_LOG_ITEM_XP_MULTIPLIER = 15;
const SKILL_PET_SEED = 0;
const COLLECTION_LOG_SEED = 100;

const COLLECTION_LOG_SKILL_PETS: readonly CollectionLogSkillPet[] = Object.freeze([
    { skill: 'a', name: 'Duece', icon: '🐕' },
    { skill: 'b', name: '01100100', icon: '🤖' },
    { skill: 'c', name: 'Lizard Doggo', icon: '🦎' },
    { skill: 'd', name: 'Ferguson', icon: '🐈‍⬛' },
    { skill: 'e', name: 'Snorm', icon: '🐌' },
    { skill: 'f', name: 'Hedwynn', icon: '🐢' },
    { skill: 'g', name: 'Speedy', icon: '🦥' },
    { skill: 'h', name: 'Lucky', icon: '🦆' },
    { skill: 'i', name: 'Big Bumbler', icon: '🐝' },
    { skill: 'j', name: 'Huck', icon: '🐁' },
    { skill: 'k', name: 'Rockford III', icon: '🪨' },
    { skill: 'l', name: 'Little Man', icon: '🐟' },
    { skill: 'm', name: 'Fwanklin', icon: '🦑' },
    { skill: 'n', name: 'Oscar', icon: '🦦' },
    { skill: 'o', name: 'Lemon', icon: '🦇' }
] as const);

const COLLECTION_LOG_ITEMS: readonly CollectionLogItem[] = Object.freeze([
    { id: 1, name: 'Charlie', icon: '🦄' },
    { id: 2, name: 'Mushu', icon: '🐉' },
    { id: 3, name: 'Gandalf', icon: '🧙‍♂️' },
    { id: 4, name: 'Flaming Chicken', icon: '🐦‍🔥' },
    { id: 5, name: 'Mr Spook', icon: '👻' },
    { id: 6, name: 'The Great Catsby', icon: '🐱' },
    { id: 7, name: 'RAWR XD', icon: '🦖' },
    { id: 8, name: 'Snek', icon: '🐍' },
    { id: 9, name: 'Bubba', icon: '🐊' },
    { id: 10, name: 'Bubbles', icon: '🐬' },
    { id: 11, name: 'The Symbol of Peace', icon: '🪷' },
    { id: 12, name: 'The Urn of Souls', icon: '🏺' },
    { id: 13, name: 'The Golden Icon', icon: '⚜️' },
    { id: 14, name: 'The Crystal Skull', icon: '💀' },
    { id: 15, name: 'The Holy Grail', icon: '🏆' },
    { id: 16, name: 'Rose', icon: '🌹' },
    { id: 17, name: 'Hibiscus', icon: '🌺' },
    { id: 18, name: 'Sunflower', icon: '🌻' },
    { id: 19, name: 'Tulip', icon: '🌷' },
    { id: 20, name: 'Lily', icon: '🪻' },
    { id: 21, name: 'Grapes', icon: '🍇' },
    { id: 22, name: 'Watermelon', icon: '🍉' },
    { id: 23, name: 'Apple', icon: '🍎' },
    { id: 24, name: 'Pear', icon: '🍐' },
    { id: 25, name: 'Blueberry', icon: '🫐' }
] as const);

export interface UseCollectionLogTrackerProps {
    userData: UserData;
    onSkillPetObtained: (pet: CollectionLogSkillPet) => void;
    onCollectionLogItemObtained: (item: CollectionLogItem) => void;
}

export interface UseCollectionLogTrackerResult {
    obtainedSkillPets: CollectionLogSkillPet[];
    obtainedCollectionLogItems: CollectionLogItem[];
}

export interface CollectionLogSkillPet {
    skill: keyof Skills;
    name: string;
    icon: string;
}

export interface CollectionLogItem {
    id: number;
    name: string;
    icon: string;
}

export function useCollectionLogTracker({ userData, onSkillPetObtained, onCollectionLogItemObtained }: UseCollectionLogTrackerProps): UseCollectionLogTrackerResult {
    const [prevUserData, setPrevUserData] = useState<UserData>(userData);

    function getUserSeededRandomXpTarget(userId: string, index: number, targetMult: number): number {
        let userCode = userId.replace(/-/g, '').split('').reverse().join('') + userId.replace(/-/g, '');

        function userChar(index: number): string {
            const charIndex = index % userCode.length;
            return userCode[charIndex];
        }

        const userSubCode = userChar(index) + userChar((index * index) + 1) + userChar((index << 1) + 2) + userChar((index >> 1) + 3) + userChar((index * 2) + index);
        return parseInt(userSubCode, 16) % (1440 * targetMult);
    }

    const skillPetXpThresholds = useMemo(() => {
        const thresholds: number[] = [];
        for (let i = 0; i < COLLECTION_LOG_SKILL_PETS.length; i++) {
            thresholds.push(getUserSeededRandomXpTarget(userData.userId, i + SKILL_PET_SEED, SKILL_PET_XP_MULTIPLIER));
        }
        return thresholds;
    }, [userData]);

    const collectionLogItemXpThresholds = useMemo(() => {
        const thresholds: number[] = [];
        for (let i = 0; i < COLLECTION_LOG_ITEMS.length; i++) {
            thresholds.push(getUserSeededRandomXpTarget(userData.userId, i + COLLECTION_LOG_SEED, COLLECTION_LOG_ITEM_XP_MULTIPLIER));
        }
        return thresholds;
    }, [userData]);

    const obtainedSkillPets = useMemo(() => {
        const obtainedPets: CollectionLogSkillPet[] = [];
        const currentUserXp = xps(userData);
        for (let i = 0; i < Object.keys(COLLECTION_LOG_SKILL_PETS).length; i++) {
            const targetThreshold = skillPetXpThresholds[i];
            const skill = COLLECTION_LOG_SKILL_PETS[i].skill;
            const currentXp = currentUserXp[skill] ?? 0;
            if (currentXp >= targetThreshold) {
                obtainedPets.push(COLLECTION_LOG_SKILL_PETS[i]);
            }
        }
        return obtainedPets;
    }, [userData]);

    const obtainedCollectionLogItems = useMemo(() => {
        const obtainedItems: CollectionLogItem[] = [];
        const currentXp = sum(xps(userData));
        for (let i = 0; i < collectionLogItemXpThresholds.length; i++) {
            const targetThreshold = collectionLogItemXpThresholds[i];
            if (currentXp >= targetThreshold) {
                obtainedItems.push(COLLECTION_LOG_ITEMS[i]);
            }
        }
        return obtainedItems;
    }, [userData]);

    useEffect(() => {
        const currentUserXp = xps(userData);
        const previousUserXp = xps(prevUserData);
        const xpChanges = add(currentUserXp, apply(previousUserXp, (skillValue) => skillValue * -1));
        const totalXpChange = sum(xpChanges);
        if (totalXpChange > 0 && currentUserXp && previousUserXp) {
            for (let i = 0; i < skillPetXpThresholds.length; i++) {
                const targetThreshold = skillPetXpThresholds[i];
                const skill = COLLECTION_LOG_SKILL_PETS[i].skill;
                const prevXp = previousUserXp[skill] ?? 0;
                const currentXp = currentUserXp[skill] ?? 0;
                if (prevXp < targetThreshold && currentXp >= targetThreshold) {
                    // Passed the threshold for a skill pet
                    onSkillPetObtained(COLLECTION_LOG_SKILL_PETS[i]);
                }
            }

            for (let i = 0; i < collectionLogItemXpThresholds.length; i++) {
                const targetThreshold = collectionLogItemXpThresholds[i];
                const prevXp = sum(previousUserXp);
                const currentXp = sum(currentUserXp);
                if (prevXp < targetThreshold && currentXp >= targetThreshold) {
                    // Passed the threshold for a collection log item
                    onCollectionLogItemObtained(COLLECTION_LOG_ITEMS[i]);
                }
            }
        }

        setPrevUserData(userData);
    }, [userData, prevUserData]);

    return useMemo(() => ({
        obtainedSkillPets,
        obtainedCollectionLogItems,
    }), [obtainedSkillPets, obtainedCollectionLogItems]);
};